import axios from "axios";
import Config from "@/config";
import { useCommonStore } from "@/store/useCommonStore";

/**
 * 构建 Form 数据请求
 * @param {object} data - 要转换的表单数据
 * @return {string} 格式化后的表单数据
 */
const prepareForForm = (data) =>
    Object.entries(data)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

/**
 * 检查请求状态，如果未认证则跳转登录
 * @param {object} response - 响应对象
 * @throws {Error} 如果状态异常
 */
const checkStatus = (response) => {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }
    if (response.status === 400) {
        return response;
    }
    if (response.status === 401) {
        localStorage.removeItem("user");
        window.location.href = `${Config.RoutePrefix}/login`;
        return; // 防止继续抛出异常
    }
    throw new Error(response.statusText || `HTTP Error: ${response.status}`);
};

/**
 * 生成 Axios 配置
 * @param {string} method - 请求方法 (GET/POST/PUT/DELETE 等)
 * @param {string} contentType - 内容类型 (json/form 等)
 * @param {object} data - 请求数据
 * @param {string} [appid] - 可选的应用 ID
 * @return {object} 配置对象
 */
const dealOption = (method, contentType, data, appid) => {
    const auth = useCommonStore.getState()?.user?.auth;
    const headers = {
        ...(auth ? { Authorization: auth.name, wsctoken: auth.token } : {}),
        ...(appid ? { "dapr-app-id": appid } : {}),
        "Content-Type":
            contentType === "form"
                ? "application/x-www-form-urlencoded"
                : "application/json",
    };

    const body =
        contentType === "form"
            ? prepareForForm(data)
            : contentType === "json"
            ? data
            : JSON.stringify(data);

    return {
        method,
        headers,
        data: body,
    };
};

/**
 * 统一的请求方法
 * @param {string} url - 请求 URL
 * @param {string} method - 请求方法 (GET/POST/PUT/DELETE 等)
 * @param {string} contentType - 内容类型 (json/form 等)
 * @param {object} data - 请求数据
 * @param {string} [appid] - 可选的应用 ID
 * @return {Promise} 请求结果
 */
export const request = async (url, method = "POST", contentType = "json", data = {}, appid) => {
    const options = dealOption(method, contentType, data, appid);
    const fullUrl = url.startsWith("http") ? url : `${Config.ApiHost}${url}`;

    try {
        const response = await axios(fullUrl, options);
        checkStatus(response);
        return response.data;
    } catch (error) {
        console.error("Request failed:", error);
        throw error;
    }
};

export default () => ({ request });
